"use strict"

import angular from "angular"
import Backoff from "backoff-promise"

import AuthService from "../auth/auth.service"
import BootstrapService from "../bootstrap/bootstrap.service"
import PlatformHeaderService from "../platform/platform-header.service"
import StorageService from "../storage/storage.service"
import CkRedirect from "../../model/domain/ck-redirect"

type HttpConfig = angular.IRequestShortcutConfig
type HttpHeaders = angular.IHttpRequestConfigHeaders
type HttpResponse<T> = angular.IHttpResponse<T>
interface HttpErrorData {
  message?: string
  code?: string
  [key: string]: unknown
}
type HttpErrorResponse = HttpResponse<HttpErrorData>

export class HttpRetry {
  private _backoff?: Backoff

  constructor(
    private delay = 1000,
    private max = 4,
    private factor = 2,
    private shouldRetry = () => false,
  ) {}

  get backoff(): Backoff {
    if (!this._backoff) {
      this._backoff = new Backoff(this.delay, 10000, this.factor, this.max)
    }
    return this._backoff
  }

  run(fn: () => angular.IPromise<unknown>): Promise<unknown> {
    return this.backoff.run(() => fn(), this.shouldRetry)
  }
}

export class HttpService {
  constructor(
    private $http: angular.IHttpService,
    private $q: angular.IQService,
    private $state: angular.ui.IStateService,
    private $stateParams: angular.ui.IStateParamsService,
    private $log: angular.ILogService,
    private AuthService: AuthService,
    private BootstrapService: BootstrapService,
    private StorageService: StorageService,
    private PlatformHeaderService: PlatformHeaderService,
  ) {
    "ngInject"
  }

  private get ready(): boolean {
    return this.BootstrapService.ready
  }

  private assertReady<T>(f: () => angular.IPromise<T>): angular.IPromise<T> {
    if (!this.ready) {
      return this.$q.reject(new Error("Waiting for bootstrap to complete"))
    }

    return f()
  }

  authGet<R = unknown>(
    url: string,
    config?: HttpConfig,
    retry?: HttpRetry,
  ): Promise<R> {
    return this.get<R>(url, this.addAuthHeader(config), retry)
  }

  optionalAuthGet<R = unknown>(
    url: string,
    config?: HttpConfig,
    retry?: HttpRetry,
  ): Promise<R> {
    if (this.AuthService.isLoggedIn) {
      return this.get<R>(url, this.addAuthHeader(config), retry)
    } else {
      return this.get<R>(url, config, retry)
    }
  }

  authPost<R = unknown, D = unknown>(
    url: string,
    data?: D,
    config?: HttpConfig,
    retry?: HttpRetry,
  ): Promise<R> {
    return this.post<R>(url, data, this.addAuthHeader(config), retry)
  }

  authPut<R = unknown, D = unknown>(
    url: string,
    data?: D,
    config?: HttpConfig,
    retry?: HttpRetry,
  ): Promise<R> {
    return this.put<R>(url, data, this.addAuthHeader(config), retry)
  }

  authDelete<R = unknown>(
    url: string,
    config?: HttpConfig,
    retry?: HttpRetry,
  ): Promise<R> {
    return this.delete<R>(url, this.addAuthHeader(config), retry)
  }

  get<R = unknown>(
    url: string,
    config?: HttpConfig,
    retry?: HttpRetry,
  ): Promise<R> {
    return this.assertReady(() => {
      config = this.PlatformHeaderService.addHeaders(url, config)
      return this.retryIfNeeded(this.$http.get.bind(this, url, config), retry)
        .then((response: any) => response.data) // eslint-disable-line @typescript-eslint/no-explicit-any
        .catch((response) => this.handleErrorResponse(response))
    }) as Promise<R>
  }

  post<R = unknown, D = unknown>(
    url: string,
    data?: D,
    config?: HttpConfig,
    retry?: HttpRetry,
  ): Promise<R> {
    return this.assertReady(() => {
      config = this.PlatformHeaderService.addHeaders(url, config)
      return this.retryIfNeeded(
        this.$http.post.bind(this, url, data, config),
        retry,
      )
        .then((response: any) => response.data) // eslint-disable-line @typescript-eslint/no-explicit-any
        .catch((response) => this.handleErrorResponse(response))
    }) as Promise<R>
  }

  put<R = unknown, D = unknown>(
    url: string,
    data?: D,
    config?: HttpConfig,
    retry?: HttpRetry,
  ): Promise<R> {
    return this.assertReady(() => {
      config = this.PlatformHeaderService.addHeaders(url, config)
      return this.retryIfNeeded(
        this.$http.put.bind(this, url, data, config),
        retry,
      )
        .then((response: any) => response.data) // eslint-disable-line @typescript-eslint/no-explicit-any
        .catch((response) => this.handleErrorResponse(response))
    }) as Promise<R>
  }

  delete<R = unknown>(
    url: string,
    config?: HttpConfig,
    retry?: HttpRetry,
  ): Promise<R> {
    return this.assertReady(() => {
      config = this.PlatformHeaderService.addHeaders(url, config)
      return this.retryIfNeeded(
        this.$http.delete.bind(this, url, config),
        retry,
      )
        .then((response: any) => response.data) // eslint-disable-line @typescript-eslint/no-explicit-any
        .catch((response) => this.handle404OrErrorResponse(response))
    }) as Promise<R>
  }

  private retryIfNeeded(
    fn: () => angular.IPromise<unknown>,
    retry?: HttpRetry,
  ): angular.IPromise<unknown> {
    if (retry) {
      return retry.run(fn)
    } else {
      return fn()
    }
  }

  private addAuthHeader(config?: HttpConfig): HttpConfig {
    if (!config) {
      config = {}
    }
    if (!config.headers) {
      config.headers = {}
    }
    config.headers.Authorization = this.AuthService.authHeader
    return config
  }

  private handle404OrErrorResponse(response: HttpErrorResponse) {
    if (response.status === 404) {
      return null
    } else {
      return this.handleErrorResponse(response)
    }
  }

  private handleErrorResponse(response: HttpErrorResponse) {
    return HttpService.handleErrorResponse(
      response,
      this.$q,
      this.$log,
      this.$state,
      this.$stateParams,
      this.AuthService,
      this.StorageService,
    )
  }

  static handleErrorResponse(
    response: HttpErrorResponse,
    $q: angular.IQService,
    $log: angular.ILogService,
    $state: angular.ui.IStateService,
    $stateParams: angular.ui.IStateParamsService,
    authService: AuthService,
    storageService: StorageService,
  ): Promise<never> {
    if (!response.data) {
      response.data = {}
    }

    const error = new HttpError(
      response.data.message || response.statusText,
      response.data,
      response.data.code,
      response.status,
      response.statusText,
      response.headers,
      response.config,
    )

    // when user is on shared-work and receives an auth error,
    // we need to display error message first instead of redirecting
    // to main login page with no error message
    if (response.status === 401 && $state.current.name !== "root.shared-work") {
      $log.error("Unauthorized token detected, redirecting to login")
      storageService.ckRedirect = new CkRedirect(
        $state.current.name,
        $stateParams,
      )
      authService.signOut()
    }
    return $q.reject(error) as Promise<never>
  }
}

class HttpError extends Error {
  constructor(
    message: string,
    public data: HttpErrorData,
    public code: string | undefined,
    public status: number,
    public statusText: string,
    public headers: HttpHeaders,
    public config: HttpConfig,
  ) {
    super(message)
  }
}
